import React, { useEffect, useState, useMemo } from 'react';
import dayjs from 'dayjs';
import {
  Table,
  Button,
  Space,
  Modal,
  Form,
  Input,
  Switch,
  message,
  Typography,
  Tag,
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { subTaskService, SubTask, CreateSubTaskData } from '../services/subTasks';
import { taskTemplateService } from '../services/taskTemplates';
import { useTableColumnSearch } from '../hooks/useTableColumnSearch';
import { useColumnVisibility } from '../hooks/useColumnVisibility';
import { ColumnVisibilityToggle } from '../components/ColumnVisibilityToggle';
import { Select } from 'antd';
import '../styles/resizable-table.css';

const { Title } = Typography;

export const SubTasks: React.FC = () => {
  const listingKey = 'sub_tasks';
  const [subTasks, setSubTasks] = useState<SubTask[]>([]);
  const [taskTemplates, setTaskTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingSubTask, setEditingSubTask] = useState<SubTask | null>(null);
  const [form] = Form.useForm<CreateSubTaskData>();

  useEffect(() => {
    fetchData();
    fetchTaskTemplates();
  }, []);

  const fetchTaskTemplates = async () => {
    try {
      const response = await taskTemplateService.getAll();
      setTaskTemplates(response.data || []);
    } catch {
      message.error('Failed to load task templates');
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await subTaskService.getAll();
      setSubTasks(response.data || []);
    } catch {
      message.error('Failed to load sub tasks data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingSubTask(null);
    form.resetFields();
    form.setFieldsValue({ enabled: true });
    setModalVisible(true);
  };

  const handleEdit = (subTask: SubTask) => {
    setEditingSubTask(subTask);
    // Backend returns task_template_id, ensure it's set in form
    form.setFieldsValue({
      name: subTask.name,
      task_template_id: subTask.task_template_id,
      enabled: subTask.enabled,
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      await subTaskService.delete(id);
      message.success('Sub task deleted');
      fetchData();
    } catch {
      message.error('Failed to delete sub task');
    }
  };

  const handleSubmit = async (values: CreateSubTaskData) => {
    const payload = {
      ...values,
      enabled: values.enabled ?? true,
    };

    try {
      if (editingSubTask) {
        await subTaskService.update(editingSubTask.id, payload);
        message.success('Sub task updated');
      } else {
        await subTaskService.create(payload);
        message.success('Sub task created');
      }
      setModalVisible(false);
      fetchData();
    } catch {
      message.error(editingSubTask ? 'Failed to update sub task' : 'Failed to create sub task');
    }
  };

  const baseColumns = [
    { 
      title: 'Name', 
      dataIndex: 'name', 
      key: 'name', 
      width: 200,
      sorter: (a: SubTask, b: SubTask) => (a.name || '').localeCompare(b.name || ''),
    },
    {
      title: 'Task Template',
      dataIndex: 'task_template',
      key: 'task_template',
      width: 200,
      render: (taskTemplate: any, record: SubTask) => {
        // Backend returns 'task_template' (snake_case), but TypeScript interface uses 'taskTemplate' (camelCase)
        const template = taskTemplate || record.taskTemplate || (record as any).task_template;
        return template?.name || '-';
      },
      sorter: (a: SubTask, b: SubTask) => {
        const aTemplate = a.taskTemplate || (a as any).task_template;
        const bTemplate = b.taskTemplate || (b as any).task_template;
        const aName = aTemplate?.name || '';
        const bName = bTemplate?.name || '';
        return aName.localeCompare(bName);
      },
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 120,
      sorter: (a: SubTask, b: SubTask) => (a.enabled ? 1 : 0) - (b.enabled ? 1 : 0),
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'green' : 'red'}>{enabled ? 'Yes' : 'No'}</Tag>
      ),
    },
    {
      title: 'Created At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 150,
      sorter: (a: SubTask, b: SubTask) => {
        if (!a.created_at && !b.created_at) return 0;
        if (!a.created_at) return 1;
        if (!b.created_at) return -1;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      },
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
    {
      title: 'Updated At',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 150,
      sorter: (a: SubTask, b: SubTask) => {
        if (!a.updated_at && !b.updated_at) return 0;
        if (!a.updated_at) return 1;
        if (!b.updated_at) return -1;
        return new Date(a.updated_at).getTime() - new Date(b.updated_at).getTime();
      },
      render: (date: string) => dayjs(date).format('DD/MM/YYYY'),
    },
  ];

  const {
    columnConfig,
    visibleColumns,
    visibleKeys,
    handleColumnVisibilityChange,
  } = useColumnVisibility(baseColumns);

  const memoizedColumns = useMemo(
    () =>
      visibleColumns.map((col) => ({
        ...col,
        width: col.width,
        getSearchValue:
          col.dataIndex === 'name'
            ? (record: SubTask) => record.name
            : col.dataIndex === 'task_template'
            ? (record: SubTask) => {
                const template = record.taskTemplate || (record as any).task_template;
                return template?.name || '';
              }
            : col.dataIndex === 'enabled'
            ? (record: SubTask) => (record.enabled ? 'Yes' : 'No')
            : col.dataIndex === 'created_at'
            ? (record: SubTask) => record.created_at
            : col.dataIndex === 'updated_at'
            ? (record: SubTask) => record.updated_at
            : (undefined as unknown as (record: SubTask) => string | number | null | undefined),
      })),
    [visibleColumns]
  );

  const { columnsWithSearch, filteredData } = useTableColumnSearch<SubTask>(
    subTasks,
    memoizedColumns as any,
    memoizedColumns as any,
    { listingKey }
  );
  const tableColumns = useMemo(() => {
    const widths = columnsWithSearch.map((col) => {
      const rawWidth = col.width;
      if (typeof rawWidth === 'number') {
        return rawWidth;
      }
      if (typeof rawWidth === 'string') {
        const parsed = Number.parseFloat(rawWidth);
        return Number.isFinite(parsed) ? parsed : 150;
      }
      return 150;
    });
    const total = widths.reduce((sum, width) => sum + width, 0) || 1;
    return columnsWithSearch.map((col, index) => ({
      ...col,
      width: `${(widths[index] / total) * 100}%`,
    }));
  }, [columnsWithSearch]);

  return (
    <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <Title level={3} style={{ margin: 0 }}>
            Sub Tasks
          </Title>
          <Space>
            <ColumnVisibilityToggle
              columns={columnConfig}
              visibleKeys={visibleKeys}
              onChange={handleColumnVisibilityChange}
            />
          <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
            New Sub Task
          </Button>
          </Space>
        </div>

        <Table
          columns={tableColumns as any}
          dataSource={filteredData}
          rowKey="id"
          loading={loading}
          pagination={false}
          footer={() => (
            <div style={{ textAlign: 'left', padding: '8px 0' }}>
              Total: {filteredData.length} {filteredData.length === 1 ? 'sub task' : 'sub tasks'}
            </div>
          )}
          onRow={(record) => ({
            onClick: () => handleEdit(record),
            style: { cursor: 'pointer' },
          })}
        />

      <Modal
        title={editingSubTask ? 'Edit Sub Task' : 'New Sub Task'}
        open={modalVisible}
        footer={null}
        onCancel={() => setModalVisible(false)}
        width={520}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="name"
            label="Sub Task Name"
            rules={[{ required: true, message: 'Please enter sub task name' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="task_template_id"
            label="Task Template"
          >
            <Select placeholder="Select Task Template" allowClear>
              {taskTemplates
                .filter(template => template.enabled)
                .map((template) => (
                  <Select.Option key={template.id} value={template.id}>
                    {template.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>

          <Form.Item name="enabled" label="Enabled" valuePropName="checked" initialValue>
            <Switch />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button onClick={() => setModalVisible(false)}>Cancel</Button>
              {editingSubTask && (
                <Button 
                  danger 
                  icon={<DeleteOutlined />}
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this sub task?')) {
                      handleDelete(editingSubTask.id);
                      setModalVisible(false);
                    }
                  }}
                >
                  Delete
                </Button>
              )}
              <Button type="primary" htmlType="submit">
                {editingSubTask ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
